import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import {
  isPushSupported,
  requestPushPermissionAndRegister,
  syncPushTokenIfAlreadyEnabled,
} from '../services/pushNotificationService';
import './PushNotificationPrompt.css';

import { Close, NotificationsNone } from '@mui/icons-material';
const DISMISSED_UNTIL_KEY = 'push_prompt_dismissed_until';
const DISMISS_DELAY_MS = 7 * 24 * 60 * 60 * 1000;
const ns = 'pushNotificationPrompt';

type PromptState = 'checking' | 'hidden' | 'prompt' | 'denied' | 'token_missing';

const isDismissedForNow = () => {
  const dismissedUntil = Number(localStorage.getItem(DISMISSED_UNTIL_KEY) || 0);
  return dismissedUntil > Date.now();
};

const PushNotificationPrompt: React.FC = () => {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const [state, setState] = useState<PromptState>('checking');
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const getPlatformLabel = useCallback(() => {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') return t(`${ns}.platform.ios`);
    if (platform === 'android') return t(`${ns}.platform.android`);
    return t(`${ns}.platform.mobile`);
  }, [t]);

  const refreshState = useCallback(async () => {
    if (!user?.pro_id || !isPushSupported() || isDismissedForNow()) {
      setState('hidden');
      return;
    }

    try {
      const perm = await PushNotifications.checkPermissions();
      if (perm.receive === 'granted') {
        await syncPushTokenIfAlreadyEnabled();
        setState(localStorage.getItem('fcm_token') ? 'hidden' : 'token_missing');
        return;
      }

      setState(perm.receive === 'denied' ? 'denied' : 'prompt');
    } catch (error) {
      console.log('[PushPrompt] état indisponible:', error);
      setState('hidden');
    }
  }, [user?.pro_id]);

  useEffect(() => {
    void refreshState();
  }, [refreshState, dismissed]);

  const copy = useMemo(() => {
    const platform = getPlatformLabel();

    if (state === 'denied') {
      return {
        title: t(`${ns}.states.denied.title`),
        description: t(`${ns}.states.denied.description`, { platform }),
        action: t(`${ns}.states.denied.action`),
      };
    }

    if (state === 'token_missing') {
      return {
        title: t(`${ns}.states.tokenMissing.title`),
        description: t(`${ns}.states.tokenMissing.description`),
        action: t(`${ns}.states.tokenMissing.action`),
      };
    }

    return {
      title: t(`${ns}.states.prompt.title`),
      description: t(`${ns}.states.prompt.description`),
      action: t(`${ns}.states.prompt.action`),
    };
  }, [state, t, getPlatformLabel]);

  const handleActivate = async () => {
    setLoading(true);
    try {
      const result = await requestPushPermissionAndRegister();
      if (result.ok) {
        localStorage.removeItem(DISMISSED_UNTIL_KEY);
        toast.success(t(`${ns}.toast.enabled`));
        setState('hidden');
      } else if (result.reason === 'denied') {
        toast.warning(t(`${ns}.toast.denied`, { platform: getPlatformLabel() }));
        setState('denied');
      } else if (result.reason === 'ios_fcm_failed') {
        toast.warning(t(`${ns}.toast.iosFcmFailed`));
        setState('token_missing');
      } else {
        toast.warning(t(`${ns}.toast.registerFailed`));
        await refreshState();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_UNTIL_KEY, String(Date.now() + DISMISS_DELAY_MS));
    setDismissed((value) => !value);
    setState('hidden');
  };

  if (state === 'checking' || state === 'hidden') return null;

  return (
    <Alert
      className="push-notification-prompt"
      severity={state === 'denied' ? 'warning' : 'info'}
      icon={<NotificationsNone />}
    >
      <div className="push-notification-prompt__content">
        <div className="push-notification-prompt__text">
          <Typography variant="body2" fontWeight={600}>
            {copy.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {copy.description}
          </Typography>
        </div>
        <Stack direction="row" spacing={1} className="push-notification-prompt__actions" alignItems="center">
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={loading}
            onClick={handleActivate}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ textTransform: 'none' }}
          >
            {copy.action}
          </Button>
          <IconButton
            size="small"
            onClick={handleDismiss}
            aria-label={t(`${ns}.dismissAriaLabel`)}
          >
            <Close />
          </IconButton>
        </Stack>
      </div>
    </Alert>
  );
};

export default PushNotificationPrompt;
